import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import logoEmpresa from '../images/logo-empresa.png';
import numeroALetras from '../utils/numeroALetras';

const fetchReciboData = async (reciboId) => {
  const reciboDoc = await getDoc(doc(db, 'SicRecibos', reciboId));

  if (!reciboDoc.exists()) {
    throw new Error('Recibo no encontrado');
  }

  return reciboDoc.data();
};

const formatCurrencyValue = (moneda, amount) => {
  if (amount === null || amount === undefined) return '₡0.00';

  const formatter = new Intl.NumberFormat(moneda === 'Dólares' ? 'en-US' : 'es-CR', {
    style: 'currency',
    currency: moneda === 'Dólares' ? 'USD' : 'CRC',
    minimumFractionDigits: 2,
  });

  return formatter.format(amount);
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Fecha no disponible';

  try {
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return 'Fecha no válida';
    }

    return date.toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return 'Fecha no válida';
  }
};

const generatePrintContent = (recibo) => {
  if (!recibo) return '';

  return `
      <div class="reporte-recibo">
        <!-- Encabezado -->
        <div class="report-header">
          <div class="header-titles">
            <div class="header-info">
              <h2><strong>RECIBO No:</strong> ${recibo.numeroRecibo}</h2>
              <p><strong>Fecha:</strong> ${formatDate(recibo.fechaRecibo)}</p>
            </div>
          </div>
          <div class="logo-container">
            <img src="${logoEmpresa}" alt="Logo de la empresa" class="logo" />
          </div>
        </div>

        <!-- Información del recibo -->
        <div class="info-recibo">
          <div class="info-grid-aligned">
            <div class="info-row">
              <span class="info-label"><strong>Recibimos de:</strong></span>
              <span class="info-value">${recibo.cliente || 'No especificado'}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>La suma de:</strong></span>
              <span class="info-value">${formatCurrencyValue(recibo.moneda, recibo.monto)}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Monto en letras:</strong></span>
              <span class="info-value">${numeroALetras(recibo.monto, recibo.moneda)}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Por concepto de:</strong></span>
              <span class="info-value">${recibo.concepto || 'No especificado'}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Forma de Pago:</strong></span>
              <span class="info-value">${recibo.formaDePago || 'No especificado'}</span>
            </div>
            ${recibo.banco ? `
            <div class="info-row">
              <span class="info-label"><strong>Banco:</strong></span>
              <span class="info-value">${recibo.banco}</span>
            </div>` : ''}
            ${recibo.numeroDeDocumento ? `
            <div class="info-row">
              <span class="info-label"><strong># Documento:</strong></span>
              <span class="info-value">${recibo.numeroDeDocumento}</span>
            </div>` : ''}
            <div class="info-row">
              <span class="info-label"><strong>Saldo Anterior:</strong></span>
              <span class="info-value">${formatCurrencyValue(recibo.moneda, recibo.saldoAnterior)}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Este Abono:</strong></span>
              <span class="info-value">${formatCurrencyValue(recibo.moneda, recibo.esteAbono)}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Saldo Actual:</strong></span>
              <span class="info-value">${formatCurrencyValue(recibo.moneda, recibo.saldoActual)}</span>
             </div>

             <div class="info-row">
               <span class="info-label"><strong>Hecho por:</strong></span>
               <span class="info-value"> ${recibo.hechoPor || 'No especificado'}</p>
             </div>

          </div>
        </div>

        <div class="separator"></div>

        <div class="footer">
          
          <p>Especialistas en Desarrollo de Sistemas, Asesorías, Venta y Reparación de Equipo de Cómputo</p>
          <p>Cédula Jurídica 3-101-145050. San Vicente, Moravia. San José. Costa Rica.</p>
          <p>Teléfono: (506) 2240-9561 / Email: info@solinme.com</p>
          <p style="margin-top: 5px;">¡Gracias por su preferencia!</p>
        </div>
      </div>
  `;
};

const buildPrintHtml = (recibo) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>RECIBO #${recibo?.numeroRecibo || ''}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 10px; /* Margen reducido para media carta */
          font-size: 10px; /* Tamaño de fuente más pequeño */
          color: #000;
        }
        .report-header {
          margin-bottom: 10px;
          margin-top: 5px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 10px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }
        .header-titles {
          flex-grow: 1;
          text-align: left;
        }
        .logo-container {
          margin-left: 10px;
          text-align: right;
        }
        .logo {
          max-width: 80px; /* Logo más pequeño */
          max-height: 80px;
        }
        .header-info {
          margin-top: 5px;
          margin-bottom: 5px;
          font-size: 10px;
          text-align: left;
        }
        h2 {
            font-size: 14px; /* Título de recibo más pequeño */
            margin: 0;
        }
        p {
            margin: 2px 0;
        }
        .info-recibo {
          background-color: #f9f9f9;
          padding: 8px;
          border: 1px solid #000;
          margin: 8px 0;
        }
        .info-grid-aligned {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-row {
          display: flex;
          align-items: baseline; /* Alinea el texto base */
        }
        .info-label {
          width: 100px; /* Ancho fijo para las etiquetas */
          text-align: right;
          padding-right: 8px;
          font-size: 10px;
          flex-shrink: 0; /* Evita que se encoja */
        }
        .info-value {
          flex: 1;
          font-size: 10px;
        }
        .separator {
          border-top: 1px solid #000;
          margin: 10px 0;
        }
        .footer {
          text-align: center;
          margin-top: 15px;
          font-size: 8px; /* Fuente más pequeña para el footer */
          color: #666;
        }
        
        /* ESTILOS PARA IMPRESIÓN */
        @media screen {
          body {
            display: none;
          }
        }
        @media print {
          body {
            display: block !important;
          }
        }
        @page {
          size: 5.5in 8.5in; /* Media carta */
          margin: 0.5cm;
        }
      </style>
    </head>
    <body>
      ${generatePrintContent(recibo)}
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => {
            window.close();
          }, 500);
        };
      </script>
    </body>
  </html>
`;

export const abrirReporteRecibo = async (reciboId) => {
  try {
    const recibo = await fetchReciboData(reciboId);
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      throw new Error('No se pudo abrir la ventana de impresión.');
    }

    printWindow.document.write(buildPrintHtml(recibo));
    printWindow.document.close();
    printWindow.focus();
  } catch (error) {
    console.error('Error generando el reporte de recibo:', error);
    alert(error.message || 'Error generando el reporte de recibo.');
  }
};

// Exportar solo lo necesario
export { fetchReciboData, formatCurrencyValue, formatDate };